/**
 * Settings Panel — Slide-out panel for reading preferences.
 */

import { useStore } from '@/store'

interface SettingsPanelProps {
  open: boolean
  onClose: () => void
}

export function SettingsPanel({ open, onClose }: SettingsPanelProps) {
  const settings = useStore((s) => s.settings)
  const updateSettings = useStore((s) => s.updateSettings)

  if (!open) return null

  const bg = settings.theme === 'dark' ? '#1e293b' : settings.theme === 'sepia' ? '#e8e0d0' : '#f1f5f9'
  const text = settings.theme === 'dark' ? '#e2e8f0' : '#1e293b'
  const border = settings.theme === 'dark' ? '#334155' : '#cbd5e1'
  const accent = settings.theme === 'dark' ? '#38bdf8' : '#2563eb'

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          zIndex: 200, cursor: 'pointer',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0,
        width: '320px', background: bg, color: text,
        borderLeft: `1px solid ${border}`,
        padding: '24px', zIndex: 201,
        overflowY: 'auto',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Settings</h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: text,
            fontSize: '20px', cursor: 'pointer', padding: '4px',
          }}>✕</button>
        </div>

        {/* Font Size */}
        <SettingRow label="Font Size" value={`${settings.fontSize}px`}>
          <input type="range" min={12} max={32} step={1}
            value={settings.fontSize}
            onChange={(e) => updateSettings({ fontSize: +e.target.value })}
            style={{ width: '100%', accentColor: accent }}
          />
        </SettingRow>

        {/* Line Height */}
        <SettingRow label="Line Height" value={`${settings.lineHeight}px`}>
          <input type="range" min={20} max={48} step={1}
            value={settings.lineHeight}
            onChange={(e) => updateSettings({ lineHeight: +e.target.value })}
            style={{ width: '100%', accentColor: accent }}
          />
        </SettingRow>

        {/* Column Width */}
        <SettingRow label="Column Width" value={`${settings.columnWidth}px`}>
          <input type="range" min={400} max={900} step={10}
            value={settings.columnWidth}
            onChange={(e) => updateSettings({ columnWidth: +e.target.value })}
            style={{ width: '100%', accentColor: accent }}
          />
        </SettingRow>

        {/* Theme */}
        <SettingRow label="Theme" value="">
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['dark', 'light', 'sepia'] as const).map((t) => (
              <button key={t} onClick={() => updateSettings({ theme: t })} style={{
                flex: 1, padding: '8px 12px', borderRadius: '8px',
                border: settings.theme === t ? `2px solid ${accent}` : `1px solid ${border}`,
                background: t === 'dark' ? '#0f172a' : t === 'sepia' ? '#f5f0e8' : '#ffffff',
                color: t === 'dark' ? '#e2e8f0' : '#1e293b',
                fontSize: '13px', cursor: 'pointer', fontWeight: settings.theme === t ? 600 : 400,
              }}>
                {t === 'dark' ? '🌙 Dark' : t === 'light' ? '☀️ Light' : '📜 Sepia'}
              </button>
            ))}
          </div>
        </SettingRow>

        {/* Gaze Highlight */}
        <SettingRow label="Gaze Highlight" value={settings.gazeHighlightEnabled ? 'On' : 'Off'}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox"
              checked={settings.gazeHighlightEnabled}
              onChange={(e) => updateSettings({ gazeHighlightEnabled: e.target.checked })}
              style={{ accentColor: accent }}
            />
            Enable gaze-following highlight
          </label>
        </SettingRow>

        {/* Gaze Intensity */}
        {settings.gazeHighlightEnabled && (
          <SettingRow label="Highlight Intensity" value={`${Math.round(settings.gazeHighlightIntensity * 100)}%`}>
            <input type="range" min={0} max={100} step={5}
              value={settings.gazeHighlightIntensity * 100}
              onChange={(e) => updateSettings({ gazeHighlightIntensity: +e.target.value / 100 })}
              style={{ width: '100%', accentColor: accent }}
            />
          </SettingRow>
        )}

        {/* Auto-Scroll */}
        <SettingRow label="Auto-Scroll" value={settings.autoScrollEnabled ? 'On' : 'Off'}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox"
              checked={settings.autoScrollEnabled}
              onChange={(e) => updateSettings({ autoScrollEnabled: e.target.checked })}
              style={{ accentColor: accent }}
            />
            Auto-scroll when gaze reaches bottom
          </label>
        </SettingRow>
      </div>
    </>
  )
}

function SettingRow({ label, value, children }: {
  label: string; value: string; children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>{label}</span>
        <span style={{ fontSize: '13px', opacity: 0.6 }}>{value}</span>
      </div>
      {children}
    </div>
  )
}
